/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import moment from "moment";
import { FileUploader } from "react-drag-drop-files";
import { CircularProgress } from "@mui/material";
import Teachr from "../../../Objects/models/Teachr";
import ImageRepository from "../../../Repositories/ImageRepository";
import Functions from "../../../Helpers/Functions";
import { describeProofDepositUploadError } from "../../../Helpers/proofDepositUploadError";
import { DV2 } from "../theme";
import { euros, studentFullName } from "../format";
import { vocab } from "../vocabulary";
import { DepositInfo } from "../types";
import { Card, FieldError, FieldLabel, Hint, TextLink, fieldStyle, useDv2Theme } from "../layout/Ui";

const FILE_TYPES = ["PDF", "PNG", "JPEG", "JPG"];

export interface DepositErrors {
    amount?: string;
    date?: string;
    attestation?: string;
    proof?: string;
}

interface Props {
    teachr: Teachr;
    child: any;
    value: DepositInfo;
    onChange: (next: DepositInfo) => void;
    /** Moitié du montant facturé : plafond de l'acompte. */
    ceiling: number;
    /** Date de validation du compte : l'acompte ne peut pas être antérieur. */
    minDate: string | null;
    errors: DepositErrors;
    /** Plusieurs séances : « pour ces cours » dans l'attestation. */
    multiple: boolean;
}

/**
 * Acompte (maquettes 1d, 1k, 2d, 2k) : interrupteur, montant et date reçus,
 * attestation sur l'honneur à certifier, preuve du versement (un seul
 * fichier, PDF ou image). Même logique d'envoi que ThirdStep : les images
 * sont réduites puis transmises comme preuve, le fichier est supprimé du
 * serveur quand on le retire.
 */
const DepositPanel = ({ teachr, child, value, onChange, ceiling, minDate, errors, multiple }: Props) => {
    const { primary, fontFamily } = useDv2Theme();
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const set = (patch: Partial<DepositInfo>) => onChange({ ...value, ...patch });

    const upload = (file: File) => {
        setUploadError("");
        setUploading(true);
        const send = (blob: Blob) =>
            ImageRepository.createImage(new File([blob], "proof-deposit", { type: "application/pdf" }))
                .then((response: any) => set({ proof: JSON.parse(JSON.stringify(response)) }))
                .catch((error: any) => setUploadError(describeProofDepositUploadError(error)))
                .finally(() => setUploading(false));
        if (file.type.search("image") === 0) {
            const ext = file.type.split("/")[1];
            Functions.resizeFile(file, true, ext).then((resized: any) => send(resized));
        } else {
            send(file);
        }
    };

    const removeProof = () => {
        const id = value.proof?.id;
        set({ proof: null });
        if (id) ImageRepository.deletImage(id).catch(() => undefined);
    };

    const amountNumber = parseFloat(value.amount) || 0;
    const dateLabel = value.date ? moment(value.date).locale("fr").format("D MMMM YYYY") : "…";

    return (
        <Card style={{ display: "flex", flexDirection: "column", gap: 18, padding: 22, fontFamily, borderColor: value.enabled ? primary : undefined, borderWidth: value.enabled ? 2 : 1 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>Le client m'a déjà versé un acompte</div>
                    <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.45, marginTop: 4 }}>
                        {value.enabled
                            ? `Le montant sera déduit de ce qu'il reste à payer à ${studentFullName(child).split(" ")[0]}.`
                            : "Une attestation sur l'honneur et un justificatif vous seront demandés."}
                    </div>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={value.enabled}
                    aria-label="Le client m'a déjà versé un acompte"
                    onClick={() => set({ enabled: !value.enabled })}
                    style={{ width: 48, height: 28, borderRadius: 14, background: value.enabled ? primary : DV2.border, border: "none", position: "relative", cursor: "pointer", flexShrink: 0, padding: 0 }}
                >
                    <span aria-hidden style={{ position: "absolute", top: 3, left: value.enabled ? 23 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.2)", transition: "left .15s" }} />
                </button>
            </div>

            {value.enabled && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <FieldLabel htmlFor="dv2-deposit-amount">Montant reçu</FieldLabel>
                            <div style={{ position: "relative" }}>
                                <input
                                    id="dv2-deposit-amount"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step="0.01"
                                    value={value.amount}
                                    onChange={(e) => set({ amount: e.target.value })}
                                    aria-invalid={!!errors.amount}
                                    aria-describedby={errors.amount ? "dv2-deposit-amount-error" : undefined}
                                    style={{ ...fieldStyle(!!errors.amount), fontFamily, paddingRight: 32 }}
                                />
                                <span aria-hidden style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 13, fontWeight: 700, color: DV2.muted }}>€</span>
                            </div>
                            {errors.amount ? <FieldError id="dv2-deposit-amount-error">{errors.amount}</FieldError> : <Hint>Au maximum {euros(ceiling)}, le reste à charge de {vocab.yourClient()}.</Hint>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <FieldLabel htmlFor="dv2-deposit-date">Reçu le</FieldLabel>
                            <input
                                id="dv2-deposit-date"
                                className="dv2-date-input"
                                type="date"
                                value={value.date ? moment(value.date).format("YYYY-MM-DD") : ""}
                                min={minDate ?? undefined}
                                max={moment().format("YYYY-MM-DD")}
                                onChange={(e) => set({ date: e.target.value ? moment(e.target.value, "YYYY-MM-DD").toISOString() : null })}
                                aria-invalid={!!errors.date}
                                aria-describedby={errors.date ? "dv2-deposit-date-error" : undefined}
                                style={{ ...fieldStyle(!!errors.date), fontFamily, fontSize: 14 }}
                            />
                            {errors.date && <FieldError id="dv2-deposit-date-error">{errors.date}</FieldError>}
                        </div>
                    </div>

                    <div style={{ background: DV2.pageBg, borderRadius: 14, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ fontWeight: 800, fontSize: 14 }}>Attestation sur l'honneur</div>
                        <div style={{ fontSize: 13, color: DV2.textSoft, lineHeight: 1.55 }}>
                            Je soussigné(e) {teachr?.firstName} {teachr?.lastName} atteste avoir reçu de {vocab.myClient()} {studentFullName(child)} la somme de <b>{amountNumber > 0 ? euros(amountNumber) : "…"}</b> le <b>{dateLabel}</b> à titre d'acompte pour {multiple ? `ces ${vocab.courses()}` : vocab.thisCourse()}. Cette attestation pourra être présentée aux organismes de contrôle et toute fausse déclaration m'expose à des sanctions.
                        </div>
                    </div>

                    <label style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={value.attestationAccepted}
                            onChange={(e) => set({ attestationAccepted: e.target.checked })}
                            aria-invalid={!!errors.attestation}
                            aria-describedby={errors.attestation ? "dv2-deposit-attestation-error" : undefined}
                            style={{ width: 22, height: 22, accentColor: primary, flexShrink: 0, marginTop: 1 }}
                        />
                        <span style={{ fontSize: 13, lineHeight: 1.5 }}>
                            Je certifie l'exactitude de cette attestation et je la signe en validant ma déclaration. <span style={{ color: DV2.muted }}>Elle sera archivée dans Mes documents et envoyée par e-mail à .</span>
                        </span>
                    </label>
                    {errors.attestation && <FieldError id="dv2-deposit-attestation-error">{errors.attestation}</FieldError>}

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <FieldLabel>Preuve du versement, un seul fichier</FieldLabel>
                        {value.proof ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 12, border: `1px solid ${DV2.border}`, borderRadius: 12, padding: "12px 14px", background: "#fff" }}>
                                <div aria-hidden style={{ width: 36, height: 42, borderRadius: 6, background: DV2.tint, border: "1px solid #C9D9EC", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, color: primary, flexShrink: 0 }}>PDF</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <a href={value.proof.contentUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 800, fontSize: 13, color: DV2.text, textDecoration: "none", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {value.proof.filePath ?? "Preuve du versement"}
                                    </a>
                                    <div style={{ fontSize: 12, color: DV2.success, fontWeight: 700 }}>Ajouté</div>
                                </div>
                                <TextLink label="Retirer" onClick={removeProof} />
                            </div>
                        ) : (
                            <FileUploader
                                handleChange={upload}
                                name="proof"
                                types={FILE_TYPES}
                                onTypeError={() => setUploadError("Format non accepté : PDF, PNG ou JPEG.")}
                                children={
                                    <div style={{ border: `1px dashed ${errors.proof || uploadError ? DV2.danger : DV2.border}`, borderRadius: 12, padding: "16px 14px", textAlign: "center", fontSize: 13, color: DV2.textSoft, background: "#fff", cursor: "pointer" }}>
                                        {uploading ? <><CircularProgress size={14} style={{ marginRight: 8, verticalAlign: "middle" }} /> Envoi du fichier…</> : (
                                            <>Glissez votre justificatif ici ou <span style={{ color: primary, fontWeight: 800 }}>choisissez un fichier</span></>
                                        )}
                                    </div>
                                }
                            />
                        )}
                        {(errors.proof || uploadError) ? <FieldError>{uploadError || errors.proof}</FieldError> : <Hint>PDF, PNG ou JPEG : virement, chèque ou reçu. Pour changer de fichier, retirez celui-ci puis ajoutez le bon.</Hint>}
                    </div>
                </>
            )}
        </Card>
    );
};

export default DepositPanel;
